import type {
  CreateCustomerBudgetInput,
  CustomerBudget,
  PatchCustomerBudgetInput,
} from '@clockwise/clients/customer-budgets';
import type { ServiceRole } from '@clockwise/clients/service-roles';
import {
  ApiError,
  BUDGET_RESET_PERIOD_LABELS,
  BUDGET_RESET_PERIODS,
  isoDateString,
  MAX_BUDGET_HOURS,
} from '@clockwise/shared';
import { DatePicker } from '@clockwise/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { customerBudgetsApi, serviceRolesApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { localTodayIso } from '@/lib/utils';
import { warnIfTruncated } from '@/lib/warn-if-truncated';
import { serviceRolePreloadQuery } from './service-role-autocomplete';
import { ServiceRoleLabel } from './service-role-label';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
  /** When set, the sheet is in edit mode for this budget. */
  budget?: CustomerBudget | null;
}

// Same query the ServiceRoleAutocomplete preloads with, so this list is
// literally the same cache entry every role picker in the app reads — one
// fetch, and no way for the two to disagree about which roles exist.
//
// Customer-facing roles only: an internal role is non-billable back-office
// work and can't carry a customer budget. The server enforces the same rule,
// so this is convenience, not the gate.
const ROLE_CATALOG_QUERY = serviceRolePreloadQuery({ internal: false, active: true });

const HOURS_RE = /^\d*\.?\d+$/;

// Strips anything that isn't a digit or a decimal point as the user types,
// and collapses a second "." rather than rejecting the keystroke outright —
// so pasting "12.5.3" lands as "12.53" instead of being dropped entirely.
// Letters, symbols, and locale separators never make it into form state, so
// there's nothing for the Zod check to catch after the fact.
const sanitizeHoursInput = (raw: string): string => {
  const digitsAndDot = raw.replace(/[^\d.]/g, '');
  const firstDot = digitsAndDot.indexOf('.');
  if (firstDot === -1) return digitsAndDot;
  return digitsAndDot.slice(0, firstDot + 1) + digitsAndDot.slice(firstDot + 1).replace(/\./g, '');
};

/**
 * True when a row carries a real allowance. Blank and 0 both mean "unbudgeted"
 * — such rows are dropped on save rather than written as a zero, because the
 * stored map has no zero entries (a role that is absent is simply unbudgeted
 * and can never be over budget).
 */
const isBudgeted = (raw: string): boolean => {
  const s = raw.trim();
  return HOURS_RE.test(s) && Number(s) > 0;
};

// Hours are entered as text so a half-typed "1." doesn't get coerced to a
// number mid-keystroke and fight the caret. Parsed on submit.
const AllocationSchema = z.object({
  serviceRoleId: z.string().uuid(),
  hours: z.string().superRefine((raw, ctx) => {
    const s = raw.trim();
    // Blank is the resting state of every unbudgeted role, so it is valid.
    if (s === '') return;
    const fail = (message: string) => ctx.addIssue({ code: z.ZodIssueCode.custom, message });
    if (!HOURS_RE.test(s)) return fail('Must be a number');
    const n = Number(s);
    // 0 is accepted at the keyboard and treated as "leave unbudgeted" on save,
    // so it must not read as an error while typing.
    if (n === 0) return;
    if (n > MAX_BUDGET_HOURS) return fail(`Must be at most ${MAX_BUDGET_HOURS}`);
    if (Number(n.toFixed(2)) !== n) return fail('At most 2 decimal places');
  }),
});

const FormSchema = z
  .object({
    resetPeriod: z.enum(BUDGET_RESET_PERIODS),
    // REQUIRED. Empty is the unpicked state, not "no engagement" — there is
    // no such budget any more. Values otherwise only come from the picker, so
    // shape validation beyond "something was chosen" would be theater.
    engagementId: z.string().min(1, 'Select the engagement this budget funds.'),
    effectiveFrom: isoDateString('Required'),
    effectiveTo: isoDateString().or(z.literal('')),
    allocations: z.array(AllocationSchema),
  })
  .refine((v) => v.effectiveTo === '' || v.effectiveTo > v.effectiveFrom, {
    message: 'End must be after start',
    path: ['effectiveTo'],
  })
  // A budget with nothing budgeted is a deleted budget — the server rejects an
  // empty allocation map, so catch it here where the message can point at the
  // list the user is looking at.
  .refine((v) => v.allocations.some((a) => isBudgeted(a.hours)), {
    message: 'Enter hours for at least one service role.',
    path: ['allocations'],
  });
type FormValues = z.infer<typeof FormSchema>;

const emptyDefaults = (): FormValues => ({
  resetPeriod: 'monthly',
  engagementId: '',
  effectiveFrom: localTodayIso(),
  effectiveTo: '',
  allocations: [],
});

export function CustomerBudgetFormSheet({ open, onOpenChange, customerId, budget }: Props) {
  const queryClient = useQueryClient();
  const isEdit = budget != null;
  // Edits follow the customer/employee versioning model: the system picks
  // when a change takes effect. Once a budget has started its start date is
  // history, and a change to its terms takes effect TODAY — the server closes
  // the stored budget there and starts a successor. The browser's own date —
  // the one the Budgets tab reads as of — sent with the save so the server
  // splits on the same day the tab will show.
  const today = localTodayIso();
  const started = budget != null && budget.effectiveFrom < today;
  const inForce = started && (budget.effectiveTo == null || budget.effectiveTo > today);

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: emptyDefaults(),
  });

  // The whole catalogue, one row per role — there is no add/remove, so every
  // role the customer could be budgeted for is on screen with an hours box.
  const catalogQuery = useQuery({
    queryKey: queryKeys.serviceRoles.list(ROLE_CATALOG_QUERY),
    queryFn: () => serviceRolesApi.list(ROLE_CATALOG_QUERY),
    staleTime: 5 * 60 * 1000,
    enabled: open,
  });
  warnIfTruncated(catalogQuery.data, 'customer-budget-form service-role list');
  const catalogRoles = useMemo<ServiceRole[]>(
    () => catalogQuery.data?.data ?? [],
    [catalogQuery.data],
  );
  const catalogTotal = catalogQuery.data?.meta?.total ?? catalogRoles.length;

  // An existing budget can reference a role that has since been retired (or
  // flipped to internal), which the catalogue query above filters out. Those
  // rows still have to appear, or saving would silently drop hours the user
  // never touched — the server replaces the map wholesale.
  const strandedIds = useMemo(() => {
    if (!budget || !catalogQuery.isSuccess) return [];
    const known = new Set(catalogRoles.map((r) => r.id));
    return Object.keys(budget.allocations)
      .filter((id) => !known.has(id))
      .sort();
  }, [budget, catalogRoles, catalogQuery.isSuccess]);

  const strandedQuery = useQuery({
    queryKey: queryKeys.serviceRoles.byIds(strandedIds),
    queryFn: () => serviceRolesApi.findByIds(strandedIds),
    enabled: open && strandedIds.length > 0,
  });

  const roleById = useMemo(() => {
    const m = new Map<string, ServiceRole>();
    for (const r of catalogRoles) m.set(r.id, r);
    for (const r of strandedQuery.data?.data ?? []) m.set(r.id, r);
    return m;
  }, [catalogRoles, strandedQuery.data]);

  // Row order: the catalogue's own name-ascending order, with any stranded
  // roles last so the live catalogue reads the same on every customer.
  const rowRoleIds = useMemo(
    () => [...catalogRoles.map((r) => r.id), ...strandedIds],
    [catalogRoles, strandedIds],
  );

  const strandedIdSet = useMemo(() => new Set(strandedIds), [strandedIds]);

  // The customer's engagements — the REQUIRED attribution picker's choices.
  // Served by the budgets surface rather than the SOW list, so a caller who
  // can write budgets can always fill the field; gating this on
  // `customer.sow.view` would leave them looking at a required control they
  // are not allowed to populate. Already deduped to the newest version of
  // each engagement, ended ones included, and sorted server-side.
  const engagementsQuery = useQuery({
    queryKey: queryKeys.customerBudgets.engagementOptions(customerId),
    queryFn: () => customerBudgetsApi.engagementOptions(customerId),
    staleTime: 5 * 60 * 1000,
    enabled: open,
  });
  const engagementOptions = useMemo(() => {
    const options = (engagementsQuery.data?.data ?? []).map((e) => ({
      id: e.id,
      name: e.name,
      sowName: e.sowName,
    }));
    // A budget can point at an engagement the list no longer carries — a
    // legacy id, or one deleted since. Dropping it would blank the trigger
    // and make the next save look like a deliberate re-attachment.
    if (budget?.engagementId && !options.some((o) => o.id === budget.engagementId)) {
      options.unshift({
        id: budget.engagementId,
        name: budget.engagementName ?? budget.engagementId,
        sowName: '',
      });
    }
    return options;
  }, [engagementsQuery.data, budget]);
  // Nothing to attach to: the customer has no engagements at all, so a budget
  // cannot be written until a SOW carries one. Distinct from a failed load.
  const noEngagements = engagementsQuery.isSuccess && engagementOptions.length === 0;

  /**
   * Display data for one row. Three distinct states, because collapsing them
   * lies to the reader (G15): resolved → chip + name; in flight → a skeleton
   * chip and '…'; settled but missing → the raw id and no chip (the hours are
   * still real and still editable).
   *
   * "In flight" requires the id to be one we actually asked for. A disabled
   * query reports `isPending`, so keying the loading state off
   * `strandedQuery.isPending` alone strands any OTHER unresolved row on '…'
   * forever — e.g. a role deleted between the catalogue landing and a later
   * refetch, which is in neither the catalogue nor `strandedIds` (those are
   * derived from the budget at open time and frozen into the rows).
   */
  const roleDisplay = (roleId: string): { code?: string | null; name: string } => {
    const role = roleById.get(roleId);
    if (role) return { code: role.shortCode || null, name: role.name };
    const inFlight = strandedIdSet.has(roleId) && strandedQuery.isPending;
    return inFlight ? { name: '…' } : { code: null, name: roleId };
  };

  // Reset once per open (and once per target budget), and only after the
  // catalogue lands — the rows ARE the catalogue, so there is nothing to seed
  // before then. Keyed rather than dependency-driven so a background refetch
  // can't wipe out half-typed hours.
  const initKey = `${budget?.id ?? 'new'}:${budget?.updatedAt ?? ''}`;
  const initializedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      initializedFor.current = null;
      return;
    }
    if (!catalogQuery.isSuccess || initializedFor.current === initKey) return;
    initializedFor.current = initKey;
    form.reset({
      resetPeriod: budget?.resetPeriod ?? 'monthly',
      engagementId: budget?.engagementId ?? '',
      effectiveFrom: budget?.effectiveFrom ?? localTodayIso(),
      effectiveTo: budget?.effectiveTo ?? '',
      allocations: rowRoleIds.map((id) => {
        const hours = budget?.allocations[id];
        return { serviceRoleId: id, hours: hours == null ? '' : String(hours) };
      }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initKey, catalogQuery.isSuccess, rowRoleIds]);

  const mutation = useMutation({
    mutationFn: async (v: FormValues) => {
      // Blank and zero rows never reach the wire: an absent role is unbudgeted,
      // which is not the same as budgeted at zero.
      const allocationMap = Object.fromEntries(
        v.allocations
          .filter((a) => isBudgeted(a.hours))
          .map((a) => [a.serviceRoleId, Number(a.hours)]),
      );
      // Always sent, on create and on patch alike: the field is required, and
      // the schema guarantees it is non-empty by the time we get here. Sending
      // it on every save also repairs a legacy budget that arrived unattached
      // — the user had to pick an engagement to get this far.
      if (isEdit && budget) {
        const payload: PatchCustomerBudgetInput = {
          resetPeriod: v.resetPeriod,
          // Replaces the stored map wholesale — the server does not merge, so
          // clearing a row is how a role gets unbudgeted.
          allocations: allocationMap,
          engagementId: v.engagementId,
          // Read-only once started, and the server refuses to move it.
          ...(started ? {} : { effectiveFrom: v.effectiveFrom }),
          effectiveTo: v.effectiveTo === '' ? null : v.effectiveTo,
        };
        return customerBudgetsApi.patch(budget.id, payload, { today });
      }
      const payload: CreateCustomerBudgetInput = {
        resetPeriod: v.resetPeriod,
        allocations: allocationMap,
        engagementId: v.engagementId,
        effectiveFrom: v.effectiveFrom,
        effectiveTo: v.effectiveTo === '' ? null : v.effectiveTo,
      };
      return customerBudgetsApi.create(customerId, payload);
    },
    onSuccess: (res) => {
      // A different id back means the server superseded the budget rather
      // than editing it in place — say from when the new terms apply.
      const superseded = isEdit && budget != null && res.data.id !== budget.id;
      toast.success(
        superseded
          ? `Budget updated. New terms apply from ${res.data.effectiveFrom}.`
          : isEdit
            ? 'Budget updated'
            : 'Budget added',
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.customerBudgets.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
      onOpenChange(false);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.fields?.length) {
        let matched = false;
        for (const fe of err.fields) {
          // Server paths for allocation problems are `allocations.<roleId>`;
          // map them back onto the array index the form actually renders, so
          // the message lands under the offending row rather than nowhere.
          if (fe.path.startsWith('allocations.')) {
            const roleId = fe.path.slice('allocations.'.length);
            const idx = form.getValues('allocations').findIndex((a) => a.serviceRoleId === roleId);
            if (idx >= 0) {
              form.setError(`allocations.${idx}.hours`, { message: fe.message });
              matched = true;
              continue;
            }
          }
          if (fe.path === 'effectiveFrom' || fe.path === 'effectiveTo') {
            form.setError(fe.path, { message: fe.message });
            matched = true;
          }
          if (fe.path === 'engagementId') {
            form.setError('engagementId', { message: fe.message });
            matched = true;
          }
        }
        if (matched) {
          toast.error('Please fix the highlighted fields');
          return;
        }
      }
      toast.error(err instanceof Error ? err.message : 'Save failed');
    },
  });

  const onSubmit = form.handleSubmit((v) => mutation.mutate(v));
  const errors = form.formState.errors;

  const { requestOpenChange, discardDialog } = useUnsavedChangesGuard({
    open,
    isDirty: form.formState.isDirty,
    isSubmitting: mutation.isPending,
    onOpenChange,
  });

  const rows = form.watch('allocations');

  return (
    <>
      <Sheet open={open} onOpenChange={requestOpenChange}>
        <SheetContent
          className="flex flex-col gap-0 sm:max-w-lg"
          // Don't autofocus: the first control is the reset-period select, and
          // opening its dropdown on mount would cover the form.
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <SheetHeader>
            <SheetTitle>{isEdit ? 'Edit budget' : 'Add budget'}</SheetTitle>
            <SheetDescription>
              {inForce
                ? 'Changes to hours, roles, reset period or engagement take effect today. The current terms stay on record up to today.'
                : started && isEdit
                  ? 'This budget has ended. Its terms and past dates are history.'
                  : isEdit
                    ? 'Update this budget.'
                    : 'Set the hours this customer is allowed per period, by service role.'}
            </SheetDescription>
          </SheetHeader>

          <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 py-3">
              {catalogQuery.isPending ? (
                // The role rows ARE the form, so nothing is editable until the
                // catalogue arrives — showing the date fields first would only
                // invite edits that the seeding reset then discards.
                <div className="space-y-3">
                  <Skeleton className="h-9 w-full" />
                  <Skeleton className="h-9 w-full" />
                  <Skeleton className="h-48 w-full" />
                </div>
              ) : catalogQuery.isError ? (
                <p className="text-sm text-destructive">
                  Couldn’t load service roles, so this budget can’t be edited right now. Close the
                  panel and try again.
                </p>
              ) : (
                <>
                  <div className="space-y-1.5">
                    <Label htmlFor="cb-engagement">Engagement</Label>
                    <Select
                      // Remount on the hydrated value (see product-form-fields'
                      // Kind select, #239): a Radix Select mounted before the
                      // seeding reset lands clears the controlled value —
                      // without the key, editing a budget silently detached
                      // its engagement.
                      key={form.watch('engagementId')}
                      value={form.watch('engagementId')}
                      onValueChange={(v) =>
                        form.setValue('engagementId', v, {
                          shouldDirty: true,
                          shouldValidate: true,
                        })
                      }
                      // Nothing to choose from — while the list is in flight,
                      // if it failed, or if the customer genuinely has no
                      // engagements. Each says so below in its own words.
                      disabled={engagementOptions.length === 0}
                    >
                      <SelectTrigger id="cb-engagement">
                        <SelectValue placeholder="Select an engagement" />
                      </SelectTrigger>
                      <SelectContent>
                        {engagementOptions.map((e) => (
                          <SelectItem key={e.id} value={e.id}>
                            {e.sowName ? `${e.sowName} — ${e.name}` : e.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {engagementsQuery.isError ? (
                      <p className="text-xs text-destructive">
                        Couldn’t load engagements, so this budget can’t be saved right now. Close
                        the panel and try again.
                      </p>
                    ) : noEngagements ? (
                      <p className="text-xs text-destructive">
                        This customer has no engagements yet. Add one to a SOW first — a budget
                        funds an engagement.
                      </p>
                    ) : errors.engagementId ? (
                      <p className="text-xs text-destructive">{errors.engagementId.message}</p>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        Required — the engagement this budget funds.
                      </p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="cb-period">Reset period</Label>
                    <Select
                      value={form.watch('resetPeriod')}
                      onValueChange={(v) =>
                        form.setValue('resetPeriod', v as FormValues['resetPeriod'], {
                          shouldDirty: true,
                          shouldValidate: true,
                        })
                      }
                    >
                      <SelectTrigger id="cb-period">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {BUDGET_RESET_PERIODS.map((p) => (
                          <SelectItem key={p} value={p}>
                            {BUDGET_RESET_PERIOD_LABELS[p]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Hours below are the allowance for <em>each</em> period, not for the whole date
                      range.
                    </p>
                  </div>

                  {/* Shared shadcn DatePicker (as the SOW / rate-card /
                      employee-cost sheets use), not the browser-native date
                      input the sheet shipped with. */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="cb-from">Effective from</Label>
                      <Controller
                        control={form.control}
                        name="effectiveFrom"
                        render={({ field }) => (
                          <DatePicker
                            id="cb-from"
                            value={field.value}
                            onChange={field.onChange}
                            disabled={started}
                            ariaInvalid={!!errors.effectiveFrom}
                          />
                        )}
                      />
                      {errors.effectiveFrom ? (
                        <p className="text-xs text-destructive">{errors.effectiveFrom.message}</p>
                      ) : started ? (
                        <p className="text-xs text-muted-foreground">
                          Fixed once the budget has started.
                        </p>
                      ) : null}
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="cb-to">Effective to</Label>
                      <Controller
                        control={form.control}
                        name="effectiveTo"
                        render={({ field }) => (
                          <DatePicker
                            id="cb-to"
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Ongoing"
                            clearable
                            ariaInvalid={!!errors.effectiveTo}
                          />
                        )}
                      />
                      {errors.effectiveTo ? (
                        <p className="text-xs text-destructive">{errors.effectiveTo.message}</p>
                      ) : (
                        <p className="text-xs text-muted-foreground">Blank = ongoing.</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Hours by service role</Label>

                    {rows.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        No customer-facing service roles exist yet, so there is nothing to budget.
                      </p>
                    ) : (
                      <div className="flex items-center gap-2 px-1 text-xs text-muted-foreground">
                        <span className="min-w-0 flex-1">Service role</span>
                        <span className="w-28 text-right">Hours per period</span>
                      </div>
                    )}

                    {rows.map((row, idx) => {
                      const rowErr = errors.allocations?.[idx];
                      const role = roleDisplay(row.serviceRoleId);
                      const hoursField = form.register(`allocations.${idx}.hours`);
                      return (
                        <div key={row.serviceRoleId} className="flex items-start gap-2">
                          <ServiceRoleLabel
                            code={role.code}
                            name={role.name}
                            className="min-w-0 flex-1 pt-2 text-sm"
                          />
                          <div className="w-28 space-y-1">
                            <Input
                              inputMode="decimal"
                              aria-label={`Hours for ${role.name}`}
                              className="text-right tabular-nums"
                              aria-invalid={!!rowErr?.hours}
                              {...hoursField}
                              onChange={(e) => {
                                e.target.value = sanitizeHoursInput(e.target.value);
                                hoursField.onChange(e);
                              }}
                            />
                            {rowErr?.hours ? (
                              <p className="text-xs text-destructive">{rowErr.hours.message}</p>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}

                    {/* Array-level error (nothing budgeted at all) has no row to
                        attach to, so it renders under the whole group. RHF files
                        an error whose path IS the array under `.root` once the
                        rows themselves are registered — reading `.message`
                        directly (as this did before) finds nothing and silently
                        drops the only message the user needs. */}
                    {errors.allocations?.root?.message ? (
                      <p className="text-xs text-destructive">{errors.allocations.root.message}</p>
                    ) : null}

                    {/* The catalogue outgrew one page: say so rather than
                        presenting a partial list as the whole catalogue. */}
                    {catalogTotal > catalogRoles.length ? (
                      <p className="text-xs text-muted-foreground">
                        Showing the first {catalogRoles.length} of {catalogTotal} service roles.
                      </p>
                    ) : null}
                  </div>
                </>
              )}
            </div>

            <SheetFooter className="flex-row justify-end border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => requestOpenChange(false)}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending || !catalogQuery.isSuccess}>
                {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Add budget'}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
      {discardDialog}
    </>
  );
}
